import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle, XCircle, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SystemAlert {
  id: string;
  alert_type: string;
  severity: string;
  title: string;
  description: string;
  created_at: string;
  resolved_at: string | null;
}

interface ErrorStats {
  totalErrors: number;
  criticalErrors: number;
  warningErrors: number;
  recentErrors: SystemAlert[];
}

export function ErrorMonitoring() {
  const [errorStats, setErrorStats] = useState<ErrorStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadErrorStats = async () => {
    setIsLoading(true);
    try {
      // Get recent system alerts
      const { data: alerts, error } = await supabase
        .from('system_alerts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error loading system alerts:', error);
        toast.error("Failed to load error monitoring data");
        return;
      }

      // Calculate stats
      const totalErrors = alerts?.length || 0;
      const criticalErrors = alerts?.filter(alert => alert.severity === 'critical').length || 0;
      const warningErrors = alerts?.filter(alert => alert.severity === 'warning').length || 0;

      setErrorStats({
        totalErrors,
        criticalErrors,
        warningErrors,
        recentErrors: alerts || []
      });

    } catch (error) {
      console.error('Error loading error stats:', error);
      toast.error("Failed to load error monitoring data");
    } finally {
      setIsLoading(false);
    }
  };

  const testErrorMonitoring = async () => {
    try {
      // Test by creating a test alert
      const { error } = await supabase.functions.invoke('system-monitoring', {
        body: {
          action: 'test_alert',
          alert_type: 'monitoring_test',
          message: 'Error monitoring system test'
        }
      });

      if (error) {
        console.error('Error testing monitoring:', error);
        toast.error("Failed to test error monitoring");
      } else {
        toast.success("Error monitoring test completed");
        loadErrorStats();
      }
    } catch (error) {
      console.error('Error testing monitoring:', error);
      toast.error("Failed to test error monitoring");
    }
  };

  useEffect(() => {
    loadErrorStats();
    
    // Set up real-time subscription for new alerts
    const subscription = supabase
      .channel('system_alerts')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'system_alerts' },
        (payload) => {
          console.log('New system alert:', payload.new);
          loadErrorStats();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Critical</Badge>;
      case 'warning':
        return <Badge variant="secondary"><AlertTriangle className="w-3 h-3 mr-1" />Warning</Badge>;
      case 'info':
        return <Badge variant="outline"><CheckCircle className="w-3 h-3 mr-1" />Info</Badge>;
      default:
        return <Badge variant="outline">{severity}</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Error Monitoring
        </CardTitle>
        <CardDescription>
          Real-time system error tracking and alerting
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {errorStats && (
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-muted rounded-md">
              <div className="text-2xl font-bold">{errorStats.totalErrors}</div>
              <div className="text-sm text-muted-foreground">Total Alerts</div>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-md">
              <div className="text-2xl font-bold text-red-600">{errorStats.criticalErrors}</div>
              <div className="text-sm text-muted-foreground">Critical</div>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-md">
              <div className="text-2xl font-bold text-yellow-600">{errorStats.warningErrors}</div>
              <div className="text-sm text-muted-foreground">Warnings</div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <h4 className="font-medium">Recent Alerts</h4>
          {errorStats?.recentErrors.length === 0 ? (
            <div className="text-center p-4 text-muted-foreground">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
              No recent alerts - system running smoothly
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {errorStats?.recentErrors.map((alert) => (
                <div key={alert.id} className="flex items-center justify-between p-2 border rounded">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {getSeverityBadge(alert.severity)}
                      <span className="text-sm font-medium">{alert.alert_type}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{alert.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>
                  {alert.resolved_at && (
                    <Badge variant="outline" className="ml-2">Resolved</Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button 
            onClick={loadErrorStats} 
            disabled={isLoading}
            variant="outline"
            className="flex-1"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? "Loading..." : "Refresh"}
          </Button>
          <Button 
            onClick={testErrorMonitoring}
            variant="secondary"
            className="flex-1"
          >
            Test Monitoring
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}